import React, { useCallback, useEffect, useState } from 'react'
import { BsArrowDownUp } from 'react-icons/bs'
import { FaArrowAltCircleLeft, FaEye, FaLock, FaLockOpen, FaRegEdit, FaRegTrashAlt, FaSearch } from 'react-icons/fa'
import { Select } from '../../Components/Employee/Select/Select'
import { FaCircleInfo } from 'react-icons/fa6'
import { Doughnut } from 'react-chartjs-2'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { payslipTranslate } from '../../i18n/translatedMessages'
import { IoArrowBackOutline, IoCloseSharp } from 'react-icons/io5'
import { HiMagnifyingGlass } from 'react-icons/hi2'
import areasMapper from '../../Utils/AreasMapper.js'
import levelsMapper from '../../Utils/LevelsMapper.js'
import { translate as translateAreas } from '../../i18n/Library/areasCorrelation.js'
import { translate as translatePosition } from '../../i18n/Library/hierarchicalLevel'
import List from '../../API/Lists/Lists.js'
import DetailsPayslip from './detailsPayslip'
import { HiCursorClick } from 'react-icons/hi'
import Payroll from '../../API/Payroll/Payroll'
import Loading from '../Loading/Loading'
import { FaSpinner } from 'react-icons/fa'

ChartJS.register(ChartDataLabels)
ChartJS.register(ArcElement, Tooltip, Legend)

const monthsInSpanish = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

const monthsInPt = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julio', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']

const PaySlipResult = ({ payslipData, onCloseResult, language }) => {
	const [filters, setFilters] = useState({
		area: '',
		level: '',
		text: '',
	})

	const [textFilter, setTextFilter] = useState('')

	const [loading, setLoading] = useState(false)
	const [loadingValidate, setLoadingValidate] = useState(false)
	const [loadingSingleValidation, setLoadingSingleValidation] = useState({})
	const [areas, setAreas] = useState([])
	const [levels, setLevels] = useState([])
	const [employeeList, setEmployeeList] = useState()
	const [originalData, setOriginalData] = useState()
	const [selectedData, setSelectedData] = useState({
		validatedBy: null,
		id: '',
	})
	const [graphicData, setGraphicData] = useState()

	const [showDetails, setShowDetails] = useState(false)

	const [selectedPayrolls, setSelectedPayrolls] = useState([])
	const nonValidatedEmployees = employeeList?.filter(item => !item.validatedBy)

	const toggleSelectAll = () => {
		if (selectedPayrolls.length === nonValidatedEmployees.length) {
			setSelectedPayrolls([])
		} else {
			setSelectedPayrolls(nonValidatedEmployees.map(item => item.id))
		}
	}

	const toggleSelect = id => {
		setSelectedPayrolls(prev => (prev.includes(id) ? prev.filter(payrollId => payrollId !== id) : [...prev, id]))
	}

	const validatePayrolls = async () => {
		setLoadingValidate(true)
		if (selectedPayrolls.length === 0) return

		const validatedUserId = JSON.parse(localStorage.getItem('user')).id
		const sendData = {
			validatedBy: validatedUserId,
			payrollIds: selectedPayrolls,
		}

		try {
			await Payroll.updateValidation(payslipData.id, sendData)

			setEmployeeList(prevList =>
				prevList.map(employee => (selectedPayrolls.includes(employee.id) ? { ...employee, validatedBy: validatedUserId } : employee))
			)

			setSelectedPayrolls([])
		} catch (error) {
			console.error('Erro ao validar:', error)
		} finally {
			setLoadingValidate(false)
		}
	}

	const handleCloseDetails = () => {
		setShowDetails(false)
	}

	const fetchData = useCallback(async () => {
		try {
			setLoading(true)
			const { area, level, text } = filters

			const [resArea, resLevel, respGraphicData, respEmployeeList] = await Promise.all([
				List.areas(),
				List.levels(),
				Payroll.getGraphicData(payslipData.id, level, area, text, '', ''),
				Payroll.getEmployees(payslipData.id, level, area, text, '', ''),
			])

			setAreas(resArea.map(areasMapper.toDomain))
			setLevels(resLevel.map(levelsMapper.toDomain))
			setEmployeeList(respEmployeeList)
			setOriginalData(respEmployeeList)
			setGraphicData(respGraphicData)
		} catch (error) {
			console.error('Error:', error)
		} finally {
			setLoading(false)
		}
	}, [filters])

	useEffect(() => {
		fetchData()
	}, [fetchData])

	const getMonthTranslate = month => {
		if (!isNaN(month)) {
			const monthNumber = parseInt(month, 10)
			if (language === 'es') return monthsInSpanish[monthNumber - 1]
			else return monthsInPt[monthNumber - 1]
		}

		const monthIndex = monthsInPt.findIndex(m => m.toLowerCase() === month?.toLowerCase())
		if (monthIndex !== -1) {
			if (language === 'es') return monthsInSpanish[monthIndex]
			else return monthsInPt[monthIndex]
		}
		return 'Mês inválido'
	}

	const data = {
		labels: ['Total Sueldo Líquido', 'Leyes Sociales', 'Impuestos', 'Descuentos', 'Aporte Empresa'],
		datasets: [
			{
				data: [
					graphicData?.totalPayable ?? 0,
					graphicData?.totalContribution ?? 0,
					graphicData?.taxes ?? 0,
					graphicData?.totalDiscounts ?? 0,
					graphicData?.entries ?? 0,
				],
				backgroundColor: ['#8979FF', '#FF928A', '#3CC3DF', '#FFAE4C', '#537FF1'],
				borderWidth: 1,
			},
		],
	}

	const optionsGraph = {
		maintainAspectRatio: false,
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				callbacks: {
					label: function (tooltipItem) {
						const dataset = tooltipItem.dataset
						const total = dataset.data.reduce((sum, value) => sum + (isNaN(value) ? 0 : value), 0)
						const currentValue = isNaN(dataset.data[tooltipItem.dataIndex]) ? 0 : dataset.data[tooltipItem.dataIndex]
						const percentage = total > 0 ? ((currentValue / total) * 100).toFixed(2) : '0.00'
						return `${tooltipItem.label}: ${percentage}% (CLP$ ${currentValue.toLocaleString('es-CL')})`
					},
				},
			},
			datalabels: {
				display: true,
				formatter: (value, ctx) => {
					const total = ctx.dataset.data.reduce((sum, val) => sum + (isNaN(val) ? 0 : val), 0)
					const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0'
					return `${percentage}%`
				},
				color: '#fff',
				font: {
					weight: 'bold',
					size: 12,
				},
			},
		},
	}

	const handleFilterText = e => {
		const text = e.target.value

		setTextFilter(text)
	}

	const handleKeyDown = e => {
		if (e.key === 'Enter') {
			setFilters(prevFilters => ({
				...prevFilters,
				text: textFilter,
			}))
		}
	}

	const handleSearchText = () => {
		setFilters(prevFilters => ({
			...prevFilters,
			text: textFilter,
		}))
	}

	const handleFilter = e => {
		const selectedOption = e.target.value

		setFilters(prev => ({
			...prev,
			[e.target.name]: selectedOption || null,
		}))
	}

	const cleanFilters = () => {
		setFilters({
			area: '',
			level: '',
			text: '',
		})
		setTextFilter('')
	}

	const translatedAreas = [...areas]
		.sort((a, b) => a.value.localeCompare(b.value))
		.map(item => ({
			...item,
			value: translateAreas[language][item?.value],
		}))

	const translatedLevels = [...levels]
		.sort((a, b) => a.value.localeCompare(b.value))
		.map(item => ({
			...item,
			value: translatePosition[language][item?.value],
		}))

	const validateSinglePayroll = async id => {
		setLoadingSingleValidation(prev => ({ ...prev, [id]: true }))

		try {
			const validatedUserId = JSON.parse(localStorage.getItem('user')).id
			const sendData = {
				validatedBy: validatedUserId,
				payrollIds: [id],
			}

			await Payroll.updateValidation(payslipData.id, sendData)

			setEmployeeList(prevList => prevList.map(employee => (employee.id === id ? { ...employee, validatedBy: validatedUserId } : employee)))
		} catch (error) {
			console.error(`Error, id: ${id}:`, error)
		} finally {
			setLoadingSingleValidation(prev => ({ ...prev, [id]: false }))
		}
	}

	const renderActions = (id, validatedBy) => (
		<div className='tw-flex tw-gap-1 xl:tw-gap-2'>
			{validatedBy ? (
				<button
					className='tw-text-white tw-w-8 tw-h-8 tw-rounded-full tw-bg-purple-500 tw-flex tw-items-center tw-justify-center border-0'
					onClick={() => [setShowDetails(true), setSelectedData({ id, validatedBy })]}
				>
					<FaEye />
				</button>
			) : (
				<button
					className='tw-text-purple-500 tw-w-8 tw-h-8 tw-rounded-full tw-bg-white tw-border tw-border-purple-500 tw-flex tw-items-center tw-justify-center'
					onClick={() => [setShowDetails(true), setSelectedData({ id, validatedBy })]}
				>
					<FaRegEdit />
				</button>
			)}
		</div>
	)

	const renderStatus = (validatedBy, id) => {
		const isValidated = !!validatedBy

		return (
			<span
				className={`tw-px-3 tw-whitespace-nowrap tw-py-1 tw-rounded-full ${
					isValidated ? 'tw-bg-blue-100 tw-text-blue-600' : 'tw-bg-gray-200 tw-border tw-border-gray-400 tw-cursor-pointer'
				}`}
				onClick={!isValidated && !loadingSingleValidation[id] ? () => validateSinglePayroll(id) : undefined}
			>
				{isValidated ? (
					'Validado'
				) : loadingSingleValidation[id] ? (
					<FaSpinner className='tw-animate-spin' />
				) : (
					<>
						<span className='2xl:tw-hidden'>
							<HiCursorClick size={14} /> Validar
						</span>
						<span className='tw-hidden 2xl:tw-inline'>Clique para validar</span>
					</>
				)}
			</span>
		)
	}

	const updateEmployeeData = updatedEmployee => {
		setEmployeeList(prevList => {
			const newList = prevList.map(emp => (emp.id === updatedEmployee.id ? { ...emp, baseSalary: updatedEmployee.baseSalary } : emp))
			return newList
		})
	}

	return (
		<>
			<div className='tw-mr-6 2xl:tw-mr-16 tw-ml-16 2xl:tw-ml-10 22xl:tw-ml-0 tw-space-y-8'>
				<div className='tw-flex tw-items-end   tw-space-x-5'>
					<div
						className='tw-text-white tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-purple-500 tw-h-8 tw-w-8'
						onClick={onCloseResult}
					>
						<IoArrowBackOutline size={20} />
					</div>

					<div className='tw-text-2xl tw-font-bold'>{getMonthTranslate(payslipData?.month)}</div>
					<div className='tw-text-gray-500 tw-font-bold'>{payslipData?.year}</div>
					<div className='tw-text-gray-500 tw-font-bold'>{payslipData?.country}</div>
					{payslipData.status === true ? (
						<div className='tw-flex tw-items-center tw-rounded-full tw-bg-gray-200 tw-px-3 tw-py-1 tw-text-sm tw-min-w-[100px]'>
							<FaLock className='tw-mr-2' size={14} />
							{payslipTranslate.payslipClosed}
						</div>
					) : (
						<div className='tw-flex tw-items-center tw-rounded-full tw-bg-green-100 tw-px-3 tw-py-1 tw-text-sm tw-min-w-[100px]'>
							<FaLockOpen className='tw-mr-2' size={14} />
							{payslipTranslate.payslipOpen}
						</div>
					)}
				</div>

				<div className='tw-w-full tw-flex tw-space-x-3 tw-items-center tw-text-sm'>
					<div className='tw-w-7/12 border border-grey tw-rounded-lg py-1 px-3 tw-flex tw-items-center tw-justify-center tw-mt-1'>
						<HiMagnifyingGlass size={22} className='text-dark-grey' />
						<input
							type='text'
							className='border border-0 p-0 m-0 ms-4 tw-w-full'
							placeholder='Buscar Colaborador'
							onKeyDown={handleKeyDown}
							onChange={handleFilterText}
							value={textFilter}
						/>
					</div>
					<div className='tw-w-1/5'>
						<Select placeholder='Área' list={translatedAreas} onChange={handleFilter} name='area' value={filters.area} />
					</div>

					<div className='tw-w-1/5'>
						<Select placeholder={'Nivel Hierárquico'} list={translatedLevels} onChange={handleFilter} name='level' value={filters.level} />
					</div>
					<button
						className={`tw-w-[9%] tw-text-sm tw-flex tw-px-1 tw-items-center tw-justify-center tw-mt-1 ${
							Object.values(filters).some(filter => filter) ? 'tw-bg-purple-500 tw-text-white' : 'tw-text-gray-400'
						}`}
						style={{ height: 45, border: '1px solid #CBD5E1', borderRadius: 8 }}
						onClick={cleanFilters}
						disabled={Object.values(filters).every(filter => filter === '')}
					>
						<FaRegTrashAlt className='me-1' size={12} />
						Limpar
					</button>
					<button
						className={`tw-w-[9%] tw-text-sm tw-flex tw-px-2 tw-items-center tw-justify-center tw-mt-1 ${
							textFilter !== '' ? 'tw-bg-purple-500 tw-text-white' : 'tw-text-gray-400'
						}`}
						style={{ height: 45, border: '1px solid #CBD5E1', borderRadius: 8 }}
						onClick={handleSearchText}
					>
						<FaSearch className='me-1' size={12} />
						Buscar
					</button>
				</div>

				{loading ? (
					<Loading />
				) : (
					<>
						{payslipData?.status === true && graphicData && (
							<>
								<div className='tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4 tw-text-sm 2xl:tw-text-base'>
									<div className='border tw-shadow-md tw-rounded-2xl tw-p-4 tw-h-'>
										<div className='tw-flex tw-items-center tw-text-gray-500 tw-mb-4 tw-font-semibold'>
											<span className='2xl:tw-hidden'>Colab. Processados</span>
											<span className='tw-hidden 2xl:tw-inline'>Colaboradores Processados</span>
											<FaCircleInfo size={14} className='tw-ml-2' />
										</div>
										<div className=' tw-text-xl 2xl:tw-text-2xl tw-font-semibold'>{employeeList.length}</div>
									</div>
									<div className='border tw-shadow-md tw-bg-blue-50 tw-rounded-2xl tw-p-4 tw-h-'>
										<div className='tw-flex tw-items-center tw-text-gray-500 tw-mb-4 tw-font-semibold'>
											Total Líquido
											<FaCircleInfo size={14} className='tw-ml-2' />
										</div>
										<div className=' tw-text-xl 2xl:tw-text-2xl tw-font-semibold'>CLP$ {graphicData.totalPayable?.toLocaleString('es-CL')}</div>
									</div>
									<div className='border tw-shadow-md tw-bg-alert-50 tw-rounded-2xl tw-p-4 tw-h-'>
										<div className='tw-flex tw-items-center tw-text-gray-500 tw-mb-4 tw-font-semibold'>
											Total Bruto
											<FaCircleInfo size={14} className='tw-ml-2' />
										</div>
										<div className=' tw-text-xl 2xl:tw-text-2xl tw-font-semibold'>CLP$ {graphicData.entries?.toLocaleString('es-CL')}</div>
									</div>
									<div className='border tw-shadow-md tw-bg-gray-100 tw-rounded-2xl tw-p-4 tw-h-'>
										<div className='tw-flex tw-items-center tw-text-gray-500 tw-mb-4 tw-font-semibold'>
											Costo Empresa
											<FaCircleInfo size={14} className='tw-ml-2' />
										</div>
										<div className=' tw-text-xl 2xl:tw-text-2xl tw-font-semibold'>
											CLP${' '}
											{(
												graphicData.totalPayable +
												graphicData.totalContribution +
												graphicData.taxes +
												graphicData.totalDiscounts +
												graphicData.entries
											)?.toLocaleString('es-CL')}
										</div>
									</div>
								</div>
								<div className='tw-w-full tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8 tw-text-sm 2xl:tw-text-base '>
									<div className='tw-border  tw-shadow-md tw-rounded-2xl tw-p-4 '>
										<div className='tw-flex tw-items-center tw-text-gray-500 tw-mb-4 tw-font-semibold'>
											Montos a Pagar
											<FaCircleInfo size={14} className='tw-ml-2' />
										</div>
										<div className='tw-flex 2xl:tw-gap-8 tw-justify-center tw-items-center 2xl:tw-flex-row tw-flex-col tw-space-y-8 2xl:tw-space-y-0'>
											<div className='tw-w-64 tw-h-64'>
												<Doughnut data={data} options={optionsGraph} />
											</div>

											<div className='tw-gap-2 2xl:tw-space-y-4 2xl:tw-flex 2xl:tw-flex-col tw-grid tw-grid-cols-2'>
												{data.labels.map((label, index) => {
													const total = data.datasets[0].data.reduce((sum, value) => sum + (isNaN(value) ? 0 : value), 0)
													const value = isNaN(data.datasets[0].data[index]) ? 0 : data.datasets[0].data[index]
													const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0'

													return (
														<div key={index} className='tw-flex tw-items-center'>
															<span
																className='tw-w-4 tw-h-4 tw-rounded-full'
																style={{ backgroundColor: data.datasets[0].backgroundColor[index] }}
															></span>
															<span className='tw-ml-2'>
																{label}
																<span className='tw-text-gray-500'> {percentage}%</span>
															</span>
														</div>
													)
												})}
											</div>
										</div>
									</div>

									<div className='tw-border tw-shadow-md tw-rounded-2xl tw-p-4'>
										<div className='tw-flex tw-items-center tw-text-gray-500 tw-mb-4 tw-font-semibold'>
											Estadísticas del Proceso
											<FaCircleInfo size={14} className='tw-ml-2' />
										</div>

										<div className='tw-space-y-2'>
											<div className='tw-grid tw-grid-cols-3 tw-gap-2 2xl:tw-gap-4 tw-px-2 tw-items-center'>
												<span className='tw-text-left tw-font-semibold'>Colaboradores Procesados</span>
												<span className='tw-text-right'>
													<span className='tw-text-gray-500 tw-mr-1 tw-inline-block tw-w-24 tw-text-right'>{employeeList.length}</span>
												</span>
											</div>

											<div className='tw-grid tw-grid-cols-2 tw-gap-2 2xl:tw-gap-4 tw-items-center tw-bg-gray-50 tw-p-2 tw-rounded-md'>
												<span className='tw-text-left tw-font-semibold'>
													<span className='2xl:tw-inline tw-hidden tw-font-semibold'>Total </span>Sueldo Líquido
												</span>
												<span className='tw-text-right tw-flex'>
													<span className='tw-text-gray-500 tw-mr-1 tw-w-24 tw-inline-block tw-text-right'>CLP$</span>
													<span className='tw-w-24 tw-inline-block tw-text-right'>{graphicData.totalPayable?.toLocaleString('es-CL') || 0}</span>
												</span>
											</div>

											<div className='tw-grid tw-grid-cols-2 tw-gap-2 2xl:tw-gap-4 tw-px-2 tw-items-center'>
												<span className='tw-text-left tw-font-semibold'>
													<span className='2xl:tw-inline tw-hidden tw-font-semibold'>Total </span>Leyes Sociales
												</span>
												<span className='tw-text-right tw-flex'>
													<span className='tw-text-gray-500 tw-mr-1 tw-w-24 tw-inline-block tw-text-right'>CLP$</span>
													<span className='tw-w-24 tw-inline-block tw-text-right'>{graphicData.totalContribution?.toLocaleString('es-CL') || 0}</span>
												</span>
											</div>

											<div className='tw-grid tw-grid-cols-2 tw-gap-2 2xl:tw-gap-4 tw-items-center tw-bg-gray-50 tw-p-2 tw-rounded-md'>
												<span className='tw-text-left tw-font-semibold'>
													<span className='2xl:tw-inline tw-hidden tw-font-semibold'>Total </span>Impuestos
												</span>
												<span className='tw-text-right tw-flex'>
													<span className='tw-text-gray-500 tw-mr-1 tw-w-24 tw-inline-block tw-text-right'>CLP$</span>
													<span className='tw-w-24 tw-inline-block tw-text-right'>{graphicData.taxes?.toLocaleString('es-CL') || 0}</span>
												</span>
											</div>

											<div className='tw-grid tw-grid-cols-2 tw-gap-2 2xl:tw-gap-4 tw-px-2 tw-items-center'>
												<span className='tw-text-left tw-font-semibold'>
													<span className='2xl:tw-inline tw-hidden tw-font-semibold'>Total </span>Otros Descuentos
												</span>
												<span className='tw-text-right tw-flex'>
													<span className='tw-text-gray-500 tw-mr-1 tw-w-24 tw-inline-block tw-text-right'>CLP$</span>
													<span className='tw-w-24 tw-inline-block tw-text-right'>{graphicData.totalDiscounts?.toLocaleString('es-CL') || 0}</span>
												</span>
											</div>

											<div className='tw-grid tw-grid-cols-2 tw-gap-2 2xl:tw-gap-4 tw-items-center tw-bg-gray-50 tw-p-2 tw-rounded-md'>
												<span className='tw-text-left tw-font-semibold'>
													<span className='2xl:tw-inline tw-hidden tw-font-semibold'>Total </span>Aportes Empresa
												</span>
												<span className='tw-text-right tw-flex'>
													<span className='tw-text-gray-500 tw-mr-1 tw-w-24 tw-inline-block tw-text-right'>CLP$</span>
													<span className='tw-w-24 tw-inline-block tw-text-right'>{graphicData.entries?.toLocaleString('es-CL') || 0}</span>
												</span>
											</div>

											<div className='tw-grid tw-grid-cols-2 tw-gap-2 2xl:tw-gap-4 tw-px-2 tw-items-center'>
												<span className='tw-text-left tw-font-semibold'>
													<span className='2xl:tw-inline tw-hidden tw-font-semibold'>Total </span>Costo Empresa
												</span>
												<span className='tw-text-right tw-flex'>
													<span className='tw-text-gray-500 tw-mr-1 tw-w-24 tw-inline-block tw-text-right'>CLP$</span>
													<span className='tw-w-24 tw-inline-block tw-text-right'>
														{(
															graphicData.totalPayable +
															graphicData.totalContribution +
															graphicData.taxes +
															graphicData.totalDiscounts +
															graphicData.entries
														)?.toLocaleString('es-CL') || 0}
													</span>
												</span>
											</div>
										</div>
									</div>
								</div>
							</>
						)}

						<div className='tw-p-2 2xl:tw-p-4 border tw-rounded-2xl'>
							<table className='tw-table-auto tw-w-full tw-border tw-border-gray-300 tw-border-collapse tw-text-sm 2xl:tw-text-base'>
								<thead>
									<tr className='tw-bg-gray-100 tw-text-left'>
										<th className='tw-p-2 2xl:tw-p-4'>
											<input
												type='checkbox'
												className='tw-w-4 tw-h-4 tw-accent-purple-500'
												checked={selectedPayrolls?.length === nonValidatedEmployees?.length}
												onChange={toggleSelectAll}
											/>
										</th>
										<th className='tw-p-2 2xl:tw-p-4 tw-cursor-pointer'>
											Colaborador
											<span className='tw-hidden 2xl:tw-inline'> y Cargo</span>
											<BsArrowDownUp size={12} className='tw-ml-2 tw-mb-1' />
										</th>
										<th className='tw-p-2 2xl:tw-p-4 tw-align-middle'>RUT</th>
										<th className='tw-p-2 2xl:tw-p-4 tw-cursor-pointer'>
											<span className='2xl:tw-hidden'>S. Base</span>
											<span className='tw-hidden 2xl:tw-inline'>Sueldo Base</span>
											<BsArrowDownUp size={12} className='tw-ml-2 tw-mb-1' />
										</th>
										<th className='tw-p-2 2xl:tw-p-4 tw-cursor-pointer'>
											<span className='2xl:tw-hidden'>T. Hab.</span>
											<span className='tw-hidden 2xl:tw-inline'>Total Haberes</span>
											<BsArrowDownUp size={12} className='tw-ml-2 tw-mb-1' />
										</th>
										<th className='tw-p-2 2xl:tw-p-4 tw-cursor-pointer'>
											Status
											<BsArrowDownUp size={12} className='tw-ml-2 tw-mb-1' />
										</th>
										<th className='tw-p-2 2xl:tw-p-4'>Acciones</th>
									</tr>
								</thead>
								<tbody>
									{employeeList &&
										employeeList.map(item => (
											<tr key={item.id} className={`tw-border-b ${selectedPayrolls.includes(item.id) ? 'tw-bg-gray-50' : ''}`}>
												<td className='tw-p-2 2xl:tw-p-4'>
													<input
														type='checkbox'
														className='tw-w-4 tw-h-4 tw-accent-purple-500 tw-cursor-pointer'
														checked={selectedPayrolls.includes(item.id)}
														onChange={() => toggleSelect(item.id)}
														disabled={!!item.validatedBy}
													/>
												</td>
												<td className='tw-p-2 2xl:tw-p-4'>
													<div className='tw-flex tw-flex-col'>
														<span className='2xl:tw-hidden tw-font-bold'>{item.name.length > 10 ? `${item.name.slice(0, 10)}...` : item.name}</span>
														<span className='2xl:tw-hidden tw-text-gray-500'>
															{item.jobPosition
																? item.jobPosition.length > 10
																	? `${item.jobPosition.slice(0, 10)}...`
																	: item.jobPosition
																: 'jobPosition'}
														</span>

														<span className='tw-hidden 2xl:tw-block tw-font-bold'>{item.name}</span>
														<span className='tw-hidden 2xl:tw-block tw-text-gray-500'>{item.jobPosition || 'jobPosition'}</span>
													</div>
												</td>
												<td className='tw-p-2 2xl:tw-p-4 tw-whitespace-nowrap'>
													{item.employeeDocument}-{item.digit}
												</td>
												<td className='tw-p-2 2xl:tw-p-4 tw-whitespace-nowrap'>
													<span className='tw-text-gray-500 tw-text-xs xl:tw-text-sm 2xl:tw-text-base'>CLP$</span> {item.baseSalary}
												</td>
												<td className='tw-p-2 2xl:tw-p-4 tw-whitespace-nowrap tw-text-left'>
													<span className='tw-text-gray-500 tw-text-xs xl:tw-text-sm 2xl:tw-text-base'>CLP$</span> {item.totalEarnings}
												</td>
												<td className='tw-p-2 2xl:tw-p-4'>{renderStatus(item.validatedBy, item.id)}</td>
												<td className='tw-p-2 2xl:tw-p-4'>{renderActions(item.id, item.validatedBy)}</td>
											</tr>
										))}
								</tbody>
							</table>
							{selectedPayrolls.length >= 1 && (
								<div
									className='tw-bg-green-100 tw-text-black tw-p-4 tw-mt-4 tw-rounded-md tw-flex tw-justify-center tw-items-center tw-text-sm 2xl:tw-text-base'
									style={{ border: '1px solid #068071', borderRadius: 8 }}
								>
									<span>Has seleccionado {selectedPayrolls.length} colaboradores. ¿Deseas validar sus nóminas?</span>
									<div className='tw-flex tw-gap-1 2xl:tw-gap-4 tw-ml-5'>
										<button
											className='tw-bg-black tw-text-white tw-px-4 tw-py-2 tw-rounded-md tw-cursor-pointer tw-flex tw-items-center'
											onClick={validatePayrolls}
											disabled={loadingValidate}
										>
											{loadingValidate ? <FaSpinner className='tw-animate-spin tw-mr-2' /> : null}
											Sí, Validar
										</button>
										<button
											className='tw-bg-transparent tw-border tw-text-green-700 tw-px-4 tw-py-2 tw-rounded-md tw-cursor-pointer'
											onClick={() => setSelectedPayrolls([])}
											style={{ border: '1px solid #068071', borderRadius: 8 }}
										>
											Cancelar Selección
										</button>
									</div>
								</div>
							)}
						</div>

						{showDetails && <DetailsPayslip onCloseDetails={handleCloseDetails} employeeData={selectedData} onUpdateEmployee={updateEmployeeData} />}
					</>
				)}
			</div>
		</>
	)
}

export default PaySlipResult
